import React, {
    createContext,
    useContext,
    useState,
    useRef,
    useCallback,
} from "react";
import { v4 as uuid } from "uuid";
import { SearchListQueryParams } from "../../services/search/search-service";
import useFeatureFlags from "../../hooks/use-feature-flags";
import { useLocation } from "react-router";
import EventsService from "utilities/services/events/events-service";
import SearchSubmittedEventRecord from "models/view-models/events/search-submitted-event.record";
import ResultClickedEventRecord from "models/view-models/events/result-clicked-event.record";
import ConversationEventRecord from "models/view-models/events/conversation-event.record";
import MessageEventRecord from "models/view-models/events/message-event.record";
import { SearchSubmittedEvent } from "models/interfaces/events/search-submitted-event";
import {
    ConversationEventAction,
    MessageEventAction,
} from "models/constants/events/chat-event-actions";

type CreateSearchEventFilterRequest =
    SearchSubmittedEvent["searchEvent"]["filters"][number];

const _30_SECONDS = 30000;

interface EventsContextType {
    createSearchSubmittedEvent: (
        searchParams: any,
        totalCount: number,
        elapsed: number
    ) => void;
    createResultClickedEvent: (
        path: string,
        chunkId: string,
        rank: number
    ) => void;
    continueSession: () => void;
    endSession: () => void;
    createConversationEvent: (
        conversationId: string,
        action: ConversationEventAction
    ) => void;
    createMessageEvent: (
        messageId: string,
        conversationId: string,
        action: MessageEventAction
    ) => void;
}

interface EventsProviderProps extends React.PropsWithChildren {
    source: (typeof SearchSource)[keyof typeof SearchSource];
}

const EventsContext = createContext<EventsContextType | undefined>(undefined);

export const SearchSource = {
    Sidenav: "sidenav",
    Bookview: "bookview",
    Notebooks: "notebooks",
} as const;

export const EventsProvider: React.FC<EventsProviderProps> = ({
    children,
    source,
}) => {
    const { useSparkyEvents } = useFeatureFlags();
    const [sessionId, setSessionId] = useState(uuid());
    const { pathname } = useLocation();
    const timeout = useRef<NodeJS.Timeout | null>(null);
    const searchSubmittedEventService = EventsService.useSearchSubmittedEvent();
    const resultClickedEventService = EventsService.useResultClickedEvent();
    const conversationEventService = EventsService.useConversationEvent();
    const messageEventService = EventsService.useMessageEvent();

    const continueSession = () => {
        if (timeout.current != null) {
            clearTimeout(timeout.current);
        }
    };

    const endSession = useCallback(() => {
        // If the timeout is already set, clear it to extend the session
        if (timeout.current != null) {
            clearTimeout(timeout.current);
        }

        timeout.current = setTimeout(() => {
            setSessionId(uuid());
        }, _30_SECONDS);
    }, []);

    const createSearchSubmittedEvent = useCallback(
        (
            searchParams: SearchListQueryParams,
            totalCount: number,
            elapsed: number
        ) => {
            if (!useSparkyEvents) return;

            const origin = truncate(`${source}:${pathname}`);

            try {
                // Create the search submitted event
                const request = new SearchSubmittedEventRecord({
                    sessionId: sessionId,
                    searchEvent: {
                        searchText: searchParams.search ?? "",
                        skip: searchParams.start ?? 0,
                        take: searchParams.take ?? 10,
                        totalCount: totalCount,
                        duration: toTimeSpan(elapsed),
                        origin,
                        filters: toSearchEventFilters(searchParams),
                    },
                });

                // Fire and forget the event
                searchSubmittedEventService.create(request);
            } catch (e) {
                console.error(
                    `Failed to create sparky search submitted event: ${e}`
                );
            }
        },
        [
            useSparkyEvents,
            source,
            pathname,
            sessionId,
            searchSubmittedEventService,
        ]
    );

    const createResultClickedEvent = useCallback(
        async (path: string, chunkId: string, rank: number) => {
            if (!useSparkyEvents) return;

            try {
                // Create the result clicked event
                const request = new ResultClickedEventRecord({
                    sessionId: sessionId,
                    resultClickedEvent: {
                        path: truncate(path, 256),
                        chunkId,
                        rank,
                    },
                });

                // Fire and forget the event
                resultClickedEventService.create(request);
            } catch (e) {
                console.error(
                    `Failed to create sparky result clicked event: ${e}`
                );
            }
        },
        [resultClickedEventService, sessionId, useSparkyEvents]
    );

    const createConversationEvent = useCallback(
        (conversationId: string, action: ConversationEventAction) => {
            if (!useSparkyEvents) return;

            try {
                void conversationEventService
                    .create(
                        new ConversationEventRecord({
                            sessionId: conversationId,
                            conversationEvent: { conversationId, action },
                        })
                    )
                    .catch((error) =>
                        console.error(
                            `Failed to create conversation event: ${error}`
                        )
                    );
            } catch (error) {
                console.error(`Failed to create conversation event: ${error}`);
            }
        },
        [conversationEventService, useSparkyEvents]
    );

    const createMessageEvent = useCallback(
        (
            messageId: string,
            conversationId: string,
            action: MessageEventAction
        ) => {
            if (!useSparkyEvents) return;

            try {
                void messageEventService
                    .create(
                        new MessageEventRecord({
                            sessionId: conversationId,
                            messageEvent: { messageId, action },
                        })
                    )
                    .catch((error) =>
                        console.error(
                            `Failed to create message event: ${error}`
                        )
                    );
            } catch (error) {
                console.error(`Failed to create message event: ${error}`);
            }
        },
        [messageEventService, useSparkyEvents]
    );

    return (
        <EventsContext.Provider
            value={{
                createSearchSubmittedEvent,
                createResultClickedEvent,
                continueSession,
                endSession,
                createConversationEvent,
                createMessageEvent,
            }}>
            {children}
        </EventsContext.Provider>
    );
};

export const useEvents = (): EventsContextType => {
    const context = useContext(EventsContext);
    if (!context) {
        throw new Error("useEvents must be used within an EventsProvider");
    }
    return context;
};

const toTimeSpan = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const milliseconds = ms % 1000;

    const seconds = totalSeconds % 60;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = totalMinutes % 60;
    const totalHours = Math.floor(totalMinutes / 60);
    const hours = totalHours % 24;
    const days = Math.floor(totalHours / 24);

    const pad = (n: number, digits = 2) => n.toString().padStart(digits, "0");
    const fraction =
        milliseconds > 0
            ? `.${Math.floor(milliseconds * 10000)
                  .toString()
                  .padStart(7, "0")}`
            : "";

    const time = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}${fraction}`;
    return days > 0 ? `${days}.${time}` : time;
};

const toSearchEventFilters = (
    searchParams: SearchListQueryParams
): { key: string; value: string }[] => {
    const filters: CreateSearchEventFilterRequest[] = [];

    Object.entries(searchParams).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        if (Array.isArray(value)) {
            value.forEach((item) => {
                filters.push({
                    key: truncate(key),
                    value: truncate(String(item)),
                });
            });
        } else {
            filters.push({
                key: truncate(key),
                value: truncate(String(value)),
            });
        }
    });

    return filters;
};

const truncate = (val: string, max = 64) =>
    val.length > max ? val.slice(0, max) : val;
